import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Grid, Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  RowActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { useOrderContext } from "Hooks/useOrderContext";
import { ClipboardTick } from "iconsax-react";
import _ from "lodash";
import { ChecklistSeal } from "Models/checklist.seal";
import { useTranslation } from "react-i18next";
import { FiPlusCircle } from "react-icons/fi";

import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import Button, { Color } from "../../../../../../../../components/Form/Button";
import { StorageStatuses } from "../../../../../../../../components/Generic/BadgeStorageBoxStatus";
import BadgeStorageStatus from "../../../../../../../../components/Generic/BadgeStorageStatus";
import Modal from "../../../../../../../../components/Generic/Modal";
import { LIFTVAN } from "../../../../../../../../constants";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Job } from "../../../../../../../../models/job";
import { JobType } from "../../../../../../../../models/jobType";
import { Order } from "../../../../../../../../models/order";
import { ItemJob } from "../../../../../../../../models/sales";
import { Searches } from "../../../../../../../../models/shipeezi.table";
import { StorageBox, StorageStatus } from "../../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import ChecklistSealsService from "../../../../../../../../services/checklist.seal.service";
import StorageBoxService from "../../../../../../../../services/storage.box.service";
import WarehousingService from "../../../../../../../../services/warehousing.service";
import { convertMetricVolume } from "../../../../../../../../util/converts";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import { ChecklistSealsKeys } from "../../../../../../../../util/queries";
import SealRulesChecklistModal, { SealRulesChecklistModalHandle } from "../SealRulesChecklistModal";
import SingleUseBoxModal, { SingleUseBoxModalHandle } from "../SingleUseBoxModal";
import UnloadStorageBoxConfirmation, {
  UnloadStorageBoxConfirmationHandler,
} from "./components/ConfirmModal";
import {
  EditIcon,
  EmptySection,
  Header,
  IconWrapper,
  Message,
  SingleUseBadge,
  Title,
  TrashIcon,
  Wrapper,
} from "./styles";

const EmptyImg = require("../../../../../../../../assets/img/empty-inbox.svg");

interface StorageBoxModalProps {
  onSubmit: (ids: number[], callback?: () => void) => void;
  isLoading?: boolean;
  job?: Order;
  updateJob: (attribute: string, value: Order) => void;
  updateActualInventory: (sbId: number) => void;
  addStorageBox?: () => void;
  removeStorageBox?: () => void;
  view?: boolean;
  storageBoxTypes?: StorageBoxType[];
  jobTypes?: JobType[];
  isLoadingData: boolean;
}

export interface StorageBoxModalHandle {
  open: (ids?: number[], isOnView?: boolean) => void;
  close: (order?: Order) => void;
}

const StorageBoxModal: ForwardRefRenderFunction<StorageBoxModalHandle, StorageBoxModalProps> = (
  props,
  ref
) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const {
    onSubmit,
    addStorageBox,
    removeStorageBox,
    updateJob,
    job,
    updateActualInventory,
    view = false,
    isLoading = false,
    storageBoxTypes,
    jobTypes,
    isLoadingData,
  } = props;
  const unloadStorageBoxRef = useRef<UnloadStorageBoxConfirmationHandler>(null);
  const singleUseBoxModalRef = useRef<SingleUseBoxModalHandle>(null);
  const checklistModalRef = useRef<SealRulesChecklistModalHandle>(null);

  const { isLoadingStorageBoxes, handleUpdateOrder } = useOrderContext();

  const measurement = getMeasurement();

  const [open, setOpen] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [storageBoxSelected, setStorageBoxSelected] = useState<StorageBox>();
  const [storageBoxes, setStorageBoxes] = useState<StorageBox[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [storageBox, setStorageBox] = useState<StorageBox>();

  const [storageBoxToDelete, setStorageBoxToDelete] = useState<StorageBox>();
  const [storageBoxesChecklistsSeals, setStorageBoxesChecklistsSeals] = useState<ChecklistSeal[]>();

  const onSearch = (filters: Searches) => {
    if (filters?.length > 0) {
      const filterAttribute = Object.keys(filters[0])?.[0];
      const filterValue = filters[0][filterAttribute];

      const filteredStorage = storageBoxes?.filter((storageBox) =>
        String(storageBox[filterAttribute as keyof StorageBox])
          ?.toLowerCase()
          ?.includes(filterValue)
      );

      setStorageBoxes(filteredStorage);
    } else {
      setStorageBoxes(job?.storageBoxes?.sort((a, b) => a.number.localeCompare(b.number)) ?? []);
    }
  };

  const onOpenModal = (ids?: number[], isOnView?: boolean) => {
    setOpen(true);
    setIsOnViewMode(isOnView ?? false);
    setStorageBoxes(job?.storageBoxes?.sort((a, b) => a.number.localeCompare(b.number)) || []);
  };

  const handleRemoveStorageBox = () => {
    setStorageBox(undefined);
  };

  const onCloseModal = (order?: Order) => {
    setOpen(false);
    setStorageBoxSelected(undefined);
    setStorageBoxes([]);
    setIsOnViewMode(false);
    setIsDeleting(false);
    handleRemoveStorageBox();

    if (order && order.id) {
      updateJob?.("all", {
        ...(order || {}),
        storageBoxes,
      });
    }
  };

  const { mutate, isLoading: loading } = useMutation(
    ["unload.all"],
    (storageBoxId: number) =>
      WarehousingService.unloadAllByJobs({
        jobs: [job as Job],
        storageBox: { id: storageBoxId } as StorageBox,
      }),
    {
      onSuccess: () => {
        onRemoveItemsFromStorageBox(storageBoxToDelete);
      },
    }
  );

  const { mutate: deleteSingleUseStorageBox, isLoading: loadingDeletingSingleUse } = useMutation(
    ["single.use.box.delete"],
    (singleUseBox: any) =>
      StorageBoxService.update(singleUseBox.id, { ...singleUseBox, isActive: false, valid: false })
  );

  const rowActions: RowActions = {
    orderBy: !view ? ["checklist", "delete"] : [],
  };

  const jobType = jobTypes?.find((jobType) => {
    let nameToCompare = "";

    if (typeof job?.jobType !== "string") {
      const jobTypeAssociated = job?.jobType as unknown as JobType;

      nameToCompare = jobTypeAssociated?.name?.toLowerCase();
    } else {
      nameToCompare = job?.jobType as string;
    }

    return jobType?.name?.toLowerCase() === nameToCompare?.toLowerCase();
  });

  const { isLoading: isLoadingChecklistSeals } = useQuery({
    queryKey: [ChecklistSealsKeys.getAll([job?.id])],
    queryFn: async () => {
      const includeCheckListSealsQuery = JSON.stringify([
        { association: "seals" },
        { association: "pictures" },
      ]);

      const response = await ChecklistSealsService.getAll({
        jobId: job?.id,
        include: includeCheckListSealsQuery,
        page: 0,
        sortBy: JSON.stringify([["id", "desc"]]),
      });

      if (response && response.data) return setStorageBoxesChecklistsSeals(response.data);
    },
    onError: () => {},
    enabled: !!job?.id,
  });

  const storageBoxTypeHasSealRule = (storageBox?: StorageBox) => {
    const storageBoxType = storageBoxTypes?.find((storageBoxType) => {
      return storageBoxType.id === storageBox?.storageBoxTypeId;
    });

    const isStorageBoxTypeRequiresSeal = storageBoxType?.isRequireChecklist;

    const storageBoxTypeSealRules = storageBoxType?.storageBoxTypeSealRules?.find(
      (storageBoxTypeSealRule) => {
        return storageBoxTypeSealRule.jobTypeId === jobType?.id;
      }
    );

    return {
      isStorageBoxTypeRequiresSeal,
      storageBoxTypeSealRules,
    };
  };

  const storageBoxChecklistSeals = (storageBoxId?: number) => {
    const checklistSeals = storageBoxesChecklistsSeals?.find((checklistSeal) => {
      return checklistSeal.storageBoxId === storageBoxId;
    });

    return checklistSeals;
  };

  const checklistSeals = storageBoxChecklistSeals(storageBox?.id);

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("checklist"),
      key: "checklist",
      buttonAndAction: (_id: number, row: StorageBox) => {
        const { isStorageBoxTypeRequiresSeal, storageBoxTypeSealRules } =
          storageBoxTypeHasSealRule(row);
        const isStorageBoxChecklistCompleted = storageBoxChecklistSeals(row.id);
        const tooltipTitle = isStorageBoxChecklistCompleted
          ? translate("checklistDone")
          : translate("checklist");

        if (!isStorageBoxTypeRequiresSeal) return null;

        return (
          <Tooltip title={tooltipTitle} placement="top">
            <IconWrapper
              onClick={() => {
                setStorageBox(row);
                checklistModalRef.current?.open({
                  sealRules: storageBoxTypeSealRules,
                });
              }}
            >
              <ClipboardTick
                size="16"
                color={isStorageBoxChecklistCompleted ? colors.success500 : colors.gray600}
              />
            </IconWrapper>
          </Tooltip>
        );
      },
    },
    {
      title: translate("delete"),
      key: "delete",
      buttonAndAction: (_id: number, row: StorageBox) => {
        let tooltipTitle = translate("delete");
        let icon = (
          <TrashIcon
            size={16}
            color="#F97066"
            onClick={() => {
              setStorageBoxToDelete(row);

              if (row.storageStatus?.name?.toLowerCase() !== StorageStatuses.EMPTY) {
                unloadStorageBoxRef?.current?.open(row.number, Number(row.id));
              } else onRemoveItemsFromStorageBox(row);
            }}
          />
        );

        if (row.storageBoxType?.name === LIFTVAN) {
          tooltipTitle = translate("edit");
          icon = (
            <EditIcon
              size={16}
              color="#475467"
              onClick={() => {
                if (job?.id) {
                  singleUseBoxModalRef?.current?.open(row.storageBoxType);
                }
              }}
            />
          );
        }

        return (
          <Tooltip title={tooltipTitle} placement="top">
            <div>{icon}</div>
          </Tooltip>
        );
      },
    },
  ];

  const onRemoveItemsFromStorageBox = (currentStorageBox?: StorageBox) => {
    const deleteSingleUse = (isSingleUse: boolean) => {
      if (isSingleUse) {
        deleteSingleUseStorageBox(currentStorageBox, {
          onSuccess: () => {
            unloadStorageBoxRef?.current?.close();
          },
        });
      } else {
        unloadStorageBoxRef?.current?.close();
      }
    };

    if (storageBoxes && currentStorageBox) {
      setIsDeleting(true);

      const { id: storageBoxId } = currentStorageBox!;

      const storageBoxClone = _.cloneDeep(storageBoxes);
      const index = storageBoxClone?.findIndex((storageBox) => storageBox?.id === storageBoxId);

      if (index !== -1) {
        storageBoxClone?.splice(index, 1);
        setStorageBoxes(storageBoxClone);
      }

      updateActualInventory(Number(storageBoxToDelete?.id));

      setStorageBoxToDelete(undefined);

      removeStorageBox?.();

      onSubmit(
        storageBoxClone?.map((storageBox) => Number(storageBox?.id)),
        () => deleteSingleUse(!!currentStorageBox.singleUseJobId)
      );
    }
  };

  const returnGridComponent = useCallback(() => {
    if (storageBoxes?.length) {
      return (
        <>
          <ShipeeziNewGrid
            data={storageBoxes}
            totalItems={storageBoxes?.length}
            columns={columns}
            emptyData={translate("noInformationToBeShown")}
            showSelection={false}
            generalActions={
              isOnViewMode
                ? {
                    orderBy: {
                      left: ["handleSearch"],
                    },
                    handleSearch: {
                      onSearch: (filters) => onSearch(filters),
                    },
                  }
                : undefined
            }
            rowActions={view ? undefined : rowActions}
            rowActionsExtra={view ? undefined : rowActionsExtra}
            positionActionsColumn="right"
            loadingData={isLoadingStorageBoxes || isLoadingData}
          />
        </>
      );
    }

    return (
      <EmptySection>
        <img src={EmptyImg} />
        <Title>{translate("itLooksLikeTheresNoDataYet")}</Title>
        <Message>{translate("startSearchingForAStorageBoxOrCreateASingleUseBox")}</Message>
      </EmptySection>
    );
  }, [storageBoxes, isLoadingStorageBoxes, isLoadingData]);

  const columns: Column[] = [
    {
      name: "number",
      title: translate("storageBoxNumber"),
      formatComponent: (value: string, storageBox: StorageBox) => {
        return (
          <>
            {value}
            {storageBox.singleUseJobId && <SingleUseBadge>{translate("singleUse")}</SingleUseBadge>}
          </>
        );
      },
    },
    {
      name: "storageBoxType",
      title: translate("type"),
      formatText: (type: StorageBoxType) => type?.name ?? "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "itemsJobs",
      title: translate("volumeAvailable"),
      hideSearch: true,
      hideSort: true,
      formatComponent: (itemJobs: ItemJob[], storageBox: StorageBox) => {
        const { liftvanConfig, storageBoxType } = storageBox;
        const capacity = liftvanConfig?.capacity || storageBoxType?.capacity || 0;

        if (itemJobs && itemJobs?.length) {
          const volume = itemJobs
            .map((itemJob) => itemJob.volume)
            .reduce((initialValue, value) => Number(initialValue) + Number(value), 0);

          if (volume)
            return `${convertMetricVolume(capacity - volume, measurement?.code, 1)} ${
              measurement?.code
            }`;
        }

        return `${convertMetricVolume(capacity, measurement?.code, 1)} ${measurement?.code}`;
      },
    },
    {
      name: "storageStatus",
      title: translate("status"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (storageStatus: StorageStatus) => {
        if (storageStatus) {
          return <BadgeStorageStatus value={storageStatus.name} />;
        }

        return "";
      },
    },
  ];

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  useEffect(() => {
    setStorageBoxes(job?.storageBoxes?.sort((a, b) => a.number.localeCompare(b.number)) ?? []);
  }, [job?.storageBoxes]);

  const handleSubmitStorageBoxModal = () => {
    if (job?.storageBoxes?.length && job?.storageBoxes?.length < storageBoxes?.length) {
      removeStorageBox?.();
    } else {
      addStorageBox?.();
    }
    onSubmit(storageBoxes?.map((storageBox) => Number(storageBox?.id)));
  };

  return (
    <Modal
      open={open}
      handleClose={() => onCloseModal()}
      title={isOnViewMode ? translate("storageBoxes") : translate("addStorageBox")}
      showActionButtons={!isOnViewMode || isDeleting}
      actionButtonProps={{
        loading: isLoadingStorageBoxes || isLoading,
        textSave: isDeleting && isOnViewMode ? translate("delete") : translate("save"),
        customColorSave: isDeleting && isOnViewMode ? Color.ERROR : Color.SUCCESS,
        onSave: () => (storageBoxes ? handleSubmitStorageBoxModal() : () => undefined),
        onCancel: () => onCloseModal(),
      }}
    >
      <Wrapper>
        {!isOnViewMode ? (
          <Header>
            <Grid container direction="row" spacing={2}>
              <Grid item sm={12} md={6} lg-={6}>
                <AutoSuggest
                  name="storagebox"
                  placeholder={translate("searchByStorageBox")}
                  noOptionsText={translate("searchByStorageBox")}
                  value={storageBoxSelected}
                  getOptionLabel={(storageBox: StorageBox) => `${storageBox.number}`}
                  getOptionSelected={(option: StorageBox, value: StorageBox) =>
                    option.id === value.id
                  }
                  onChange={(value: StorageBox) => {
                    setStorageBoxSelected(undefined);
                    if (value) {
                      setStorageBoxes((state) => [...(state || []), value]);
                      handleUpdateOrder("storageBoxes", [...(job?.storageBoxes || []), value]);
                    }
                  }}
                  onSearch={(value) =>
                    StorageBoxService.getAll({
                      userId: currentUser?.id,
                      isActive: 1,
                      isFleet: 0,
                      singleUse: false,
                      companyId: currentUser?.companyId,
                      sortBy: JSON.stringify([["updatedAt", "desc"]]),
                      include: JSON.stringify([
                        { association: "storageStatus" },
                        { association: "storageBoxType" },
                        { association: "itemsJobs" },
                        {
                          association: "branches",
                          attributes: ["id", "name", "companyId"],
                          where: {
                            id: currentUser?.branches?.map((branch) => branch.id),
                            companyId: currentUser?.companyId,
                          },
                          required: false,
                        },
                      ]),
                      searchByOr: value ? JSON.stringify([{ number: value }]) : "",
                    })
                  }
                  clearOptionsOnEscape
                />
              </Grid>
              <Grid item sm={12} md={6} lg-={6}>
                <Button
                  data-cy="buttonCreateSingleUseBox"
                  customColor={Color.SECONDARY}
                  onClick={() => {
                    singleUseBoxModalRef?.current?.open();
                  }}
                >
                  <FiPlusCircle size={18} className="mr-3" />
                  {translate("newSingleUseBox")}
                </Button>
              </Grid>
            </Grid>
          </Header>
        ) : null}
        {returnGridComponent()}
        <UnloadStorageBoxConfirmation
          ref={unloadStorageBoxRef}
          isLoading={loading || isLoading || loadingDeletingSingleUse}
          onSubmit={(storageBoxId: number) => mutate(storageBoxId)}
        />
        <SingleUseBoxModal job={job} ref={singleUseBoxModalRef} />
        <SealRulesChecklistModal
          job={job}
          ref={checklistModalRef}
          handleRemoveStorageBox={handleRemoveStorageBox}
          isLoadingChecklistSeals={isLoadingChecklistSeals}
          jobType={jobType}
          storageBox={storageBox}
          storageBoxType={storageBox?.storageBoxType}
          checklistSeals={checklistSeals}
          setStorageBoxesChecklistsSeals={setStorageBoxesChecklistsSeals}
        />
      </Wrapper>
    </Modal>
  );
};

export default forwardRef(StorageBoxModal);
